import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Search,
  Bell,
  HelpCircle,
  ChevronDown,
  CheckCheck,
  X,
  ShoppingBag,
  Package,
  AlertCircle,
} from 'lucide-react'
import { apiGet, apiPost } from '../lib/api'

interface TopbarProps {
  title: string
  subtitle?: string
  actions?: React.ReactNode
}

interface NotificationItem {
  id: string
  type: string
  title: string
  message: string | null
  linkUrl: string | null
  icon: string | null
  readAt: string | null
  createdAt: string
}

interface NotificationsResp {
  total: number
  unreadCount: number
  data: NotificationItem[]
}

function timeAgo(iso: string): string {
  try {
    const then = new Date(iso).getTime()
    const diff = Math.floor((Date.now() - then) / 1000)
    if (diff < 60) return 'ahora'
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`
    if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`
    return new Date(iso).toLocaleDateString('es-MX')
  } catch {
    return ''
  }
}

function iconForType(type: string, hint: string | null) {
  const key = (hint || type || '').toLowerCase()
  if (key.includes('order') || key.includes('shopping') || key === 'shopping-bag') {
    return <ShoppingBag className="size-4" />
  }
  if (key.includes('stock') || key === 'package') return <Package className="size-4" />
  return <AlertCircle className="size-4" />
}

export function Topbar({ title, subtitle, actions }: TopbarProps) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- Notificaciones ---
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unread, setUnread] = useState(0)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const esRef = useRef<EventSource | null>(null)

  function goSearch(term: string) {
    const t = term.trim()
    if (!t) return
    navigate({ to: '/productos', search: { q: t } as any })
  }

  // Debounce 400ms tras dejar de escribir
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q.trim()) return
    debounceRef.current = setTimeout(() => {
      goSearch(q)
    }, 400)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (debounceRef.current) clearTimeout(debounceRef.current)
      goSearch(q)
    }
  }

  // Cargar notificaciones iniciales + abrir SSE
  useEffect(() => {
    let cancelled = false
    async function loadInitial() {
      try {
        const data = await apiGet<NotificationsResp>('/api/admin/notifications?pageSize=10')
        if (cancelled) return
        setItems(data.data)
        setUnread(data.unreadCount)
      } catch {
        /* silencioso (puede no estar logueado todavía) */
      }
    }
    loadInitial()

    // SSE para notificaciones en tiempo real
    try {
      const es = new EventSource('/api/admin/notifications/stream', { withCredentials: true })
      esRef.current = es
      es.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data) as NotificationItem
          setItems((prev) => {
            if (prev.some((p) => p.id === payload.id)) return prev
            return [payload, ...prev].slice(0, 10)
          })
          if (!payload.readAt) setUnread((u) => u + 1)
        } catch {
          /* skip */
        }
      }
      es.onerror = () => {
        // El navegador hace auto-reconnect; no cerramos nada
      }
    } catch {
      /* sin SSE */
    }

    return () => {
      cancelled = true
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
    }
  }, [])

  // Cerrar dropdown al click afuera
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!dropdownRef.current) return
      if (!dropdownRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  async function markAllRead() {
    try {
      await apiPost('/api/admin/notifications/mark-read', { all: true })
      setItems((prev) =>
        prev.map((p) => ({ ...p, readAt: p.readAt || new Date().toISOString() })),
      )
      setUnread(0)
    } catch {
      /* silencioso */
    }
  }

  async function markOneRead(id: string) {
    try {
      await apiPost('/api/admin/notifications/mark-read', { ids: [id] })
      setItems((prev) =>
        prev.map((p) => (p.id === id && !p.readAt ? { ...p, readAt: new Date().toISOString() } : p)),
      )
      setUnread((u) => Math.max(0, u - 1))
    } catch {
      /* silencioso */
    }
  }

  function onClickItem(n: NotificationItem) {
    if (!n.readAt) void markOneRead(n.id)
    if (n.linkUrl) {
      setOpen(false)
      navigate({ to: n.linkUrl as any })
    }
  }

  return (
    <header className="sticky top-0 z-10 mx-6 mt-0 rounded-2xl border border-ink-200 bg-white shadow-sm">
      <div className="flex min-h-16 items-center gap-4 px-5 py-4">
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-ink-900">{title}</h1>
          {subtitle && <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p>}
        </div>

        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar productos, SKU, clientes…"
            className="w-72 rounded-lg border border-ink-200 bg-ink-50 py-1.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-brand-500 focus:bg-white"
          />
          <span className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-ink-200 bg-white px-1.5 font-mono text-[10px] text-ink-500 lg:inline">
            ⌘K
          </span>
        </div>

        <button
          type="button"
          className="rounded-lg p-2 text-ink-700 transition-colors hover:bg-ink-100"
          title="Ayuda"
        >
          <HelpCircle className="size-4" />
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="relative rounded-lg p-2 text-ink-700 transition-colors hover:bg-ink-100"
            title="Notificaciones"
          >
            <Bell className="size-4" />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>

          {open && (
            <div className="absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-lg">
              <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-ink-900">Notificaciones</div>
                  <div className="text-xs text-ink-500">
                    {unread > 0 ? `${unread} sin leer` : 'Estás al día'}
                  </div>
                </div>
                <div className="flex gap-1">
                  {unread > 0 && (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-600 hover:bg-ink-100"
                      title="Marcar todas como leídas"
                    >
                      <CheckCheck className="size-3.5" /> Marcar todas
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
                    title="Cerrar"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="px-4 py-10 text-center text-xs text-ink-500">
                    Sin notificaciones aún
                  </div>
                ) : (
                  <ul className="divide-y divide-ink-100">
                    {items.map((n) => {
                      const isUnread = !n.readAt
                      return (
                        <li key={n.id}>
                          <button
                            type="button"
                            onClick={() => onClickItem(n)}
                            className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-ink-50 ${
                              isUnread ? 'bg-brand-50/40' : ''
                            }`}
                          >
                            <span
                              className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg ${
                                isUnread ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-600'
                              }`}
                            >
                              {iconForType(n.type, n.icon)}
                            </span>
                            <span className="flex-1">
                              <span className="block text-sm font-medium text-ink-900">{n.title}</span>
                              {n.message && (
                                <span className="mt-0.5 block text-xs text-ink-600">{n.message}</span>
                              )}
                              <span className="mt-1 block text-[11px] text-ink-500">
                                {timeAgo(n.createdAt)}
                              </span>
                            </span>
                            {isUnread && (
                              <span className="mt-1 size-2 shrink-0 rounded-full bg-brand-600" />
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 border-t border-ink-100 bg-ink-50/50 px-6 py-2.5 text-xs text-ink-500">
        <span>Maznet</span>
        <ChevronDown className="size-3 -rotate-90" />
        <span className="font-medium text-ink-900">{title}</span>
      </div>
    </header>
  )
}
